"use client";
import { useAuthMutations } from "@/api/hooks/authHooks";
import { linkSocialAccountByTicket } from "@/api/auth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { getHomePath } from "@/lib/workspace-routing";
import Link from "next/link";
import { AuthBrandMark } from "@/components/auth/brand-mark";

import { createLoginSchema } from "@membentry/shared-validations";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ComponentProps } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { FormInput } from "./FormInput";
import { useTranslations } from "next-intl";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { getApiError } from "@membentry/shared-api";
import type { UserDetailDTO } from "@membentry/shared-types";
import { reportSocialAuthFailure } from "@/lib/report-social-auth-failure";
import { validateAuthReturnPath } from "@/lib/auth-return-path";
import { MfaCodeInput } from "./mfa-code-input";
import { AppleSignInButton, GoogleSignInButton } from "./auth/social-buttons";
import {
  consumeGoogleAccountConflictFeedback,
  consumeGoogleMfaHandoff,
  consumeSocialConflictLinkHandoff,
  type SocialConflictLinkHandoff,
} from "@/lib/social-auth";

const LOGIN_ERROR_KEY_BY_CODE: Readonly<Record<string, string>> = {
  InvalidCredentials: "auth.loginError",
  InvalidLogin: "auth.loginError",
  Unauthorized: "auth.loginError",
  AccountDeactivated: "auth.accountDeactivated",
};

function resolveLoginErrorKey(error: unknown): string {
  const code = getApiError(error)?.code;
  return (code && LOGIN_ERROR_KEY_BY_CODE[code]) || "auth.loginFailed";
}

export function LoginForm({ className, ...props }: ComponentProps<"div">) {
  const t = useTranslations();
  const params = useParams();
  const locale = params.locale as string;
  const searchParams = useSearchParams();
  const loginSchema = createLoginSchema((key, values) =>
    t(`validation.${key}` as never, values),
  );
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaEmail, setMfaEmail] = useState("");
  const [showAccountConflict, setShowAccountConflict] = useState(false);
  const [conflictExpired, setConflictExpired] = useState(false);
  const [linkContext, setLinkContext] = useState<SocialConflictLinkHandoff | null>(null);
  const [linkPending, setLinkPending] = useState(false);
  const [loginErrorKey, setLoginErrorKey] = useState<string | null>(null);

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      usernameOrEmail: "",
      password: "",
    },
  });

  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState("");

  const router = useRouter();
  const { loginMutation, mfaVerifyMutation } = useAuthMutations();

  useEffect(() => {
    const email = searchParams.get("email");
    if (email) {
      loginForm.setValue("usernameOrEmail", email);
    }

    // Google MFA and conflict handoffs are one-shot and keep email/PII out of
    // callback URLs and browser/server history.
    if (searchParams.get("mfa") === "required") {
      const handoffEmail = consumeGoogleMfaHandoff();
      if (handoffEmail) {
        setMfaRequired(true);
        setMfaSetupRequired(false);
        setMfaEmail(handoffEmail);
      }
    }

    if (searchParams.get("social") === "account-conflict") {
      const handoff = consumeSocialConflictLinkHandoff();
      if (handoff) {
        const expiresAt = Date.parse(handoff.expiresAt);
        if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
          setConflictExpired(true);
          setShowAccountConflict(true);
          consumeGoogleAccountConflictFeedback();
        } else {
          setLinkContext(handoff);
          setShowAccountConflict(false);
          loginForm.setValue("usernameOrEmail", handoff.email);
          queueMicrotask(() => loginForm.setFocus("password"));
        }
      } else if (consumeGoogleAccountConflictFeedback()) {
        setShowAccountConflict(true);
        loginForm.setFocus("usernameOrEmail");
      }
    }
  }, [loginForm, searchParams]);

  const routeToWorkspace = useCallback((user: UserDetailDTO | null | undefined) => {
    router.push(getHomePath(locale, user));
  }, [locale, router]);

  const linkThenRoute = useCallback(async (
    user: UserDetailDTO | null | undefined,
    context: SocialConflictLinkHandoff,
  ) => {
    setLinkPending(true);
    try {
      await linkSocialAccountByTicket(context.provider, context.linkTicket);
      toast.success(t("auth.social.linked", {
        provider: context.provider === "google" ? "Google" : "Apple",
      }));
    } catch (error) {
      // The ticket is deliberately never passed to reporting metadata.
      reportSocialAuthFailure("session", error, context.provider);
      const code = getApiError(error)?.code;
      const key = code === "LinkTicketExpired" ? "auth.social.expired"
        : code === "LinkTicketAlreadyUsed" ? "auth.social.used"
          : code === "InvalidLinkTicket" ? "auth.social.invalid"
            : "auth.social.linkFailed";
      toast.error(t(key as never));
    } finally {
      setLinkPending(false);
      setLinkContext(null);
      routeToWorkspace(user);
    }
  }, [routeToWorkspace, t]);

  function onLoginSubmit(values: z.infer<typeof loginSchema>) {
    const submittedValues = linkContext
      ? { ...values, usernameOrEmail: linkContext.email }
      : values;
    setLoginErrorKey(null);
    loginMutation.mutate(submittedValues, {
      onError: (error) => {
        const key = resolveLoginErrorKey(error);
        setLoginErrorKey(key);
        toast.error(t(key as never));
      },
      onSuccess: (data) => {
        if (data.success) {
          if (linkContext) {
            void linkThenRoute(data.authResponse.user, linkContext);
          } else {
            const returnTo = validateAuthReturnPath(searchParams.get("returnTo"));
            router.push(returnTo ?? getHomePath(locale, data.authResponse.user));
          }
        } else if ("requiresMfaSetup" in data) {
          const returnTo = validateAuthReturnPath(searchParams.get("returnTo"));
          window.location.href = `/${locale}/me/mfa${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`;
        } else if ("requiresMfa" in data) {
          setMfaRequired(true);
          setMfaSetupRequired(false);
          setMfaEmail(data.email || "");
        }
      },
    });
  }

  function onMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaEmail) return;

    if (mfaCode.length !== 6) {
      setMfaError(t("validation.invalidMfaCode" as never));
      return;
    }

    if (!/^\d{6}$/.test(mfaCode)) {
      setMfaError(t("validation.invalidMfaCode" as never));
      return;
    }

    setMfaError("");
    mfaVerifyMutation.mutate(
      { email: mfaEmail, code: mfaCode },
      {
        onSuccess: (data) => {
          if (linkContext) {
            void linkThenRoute(data.user, linkContext);
          } else {
            const returnTo = validateAuthReturnPath(searchParams.get("returnTo"));
            router.push(returnTo ?? getHomePath(locale, data.user));
          }
        },
      },
    );
  }

  if (mfaSetupRequired) {
    return (
      <div className={cn("flex flex-col gap-7 sm:gap-8", className)} {...props}>
        <AuthBrandMark />
        <Card className="glass-panel border-white/60">
          <CardHeader>
            <CardTitle><h1>{t("auth.mfa.setupTitle")}</h1></CardTitle>
            <CardDescription>{t("auth.mfa.setupDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("auth.mfa.setupRequired")}
              </p>
              <Button
                className="w-full"
                onClick={() => {
                  window.location.href = `/${locale}/me/mfa`;
                }}
              >
                {t("auth.mfa.goToSetup")}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  setMfaSetupRequired(false);
                  setMfaEmail("");
                  loginForm.reset();
                }}
              >
                {t("auth.mfa.backToLogin")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (mfaRequired) {
    return (
      <div className={cn("flex flex-col gap-7 sm:gap-8", className)} {...props}>
        <AuthBrandMark />
        <Card className="glass-panel border-white/60">
          <CardHeader>
            <CardTitle><h1>{t("auth.mfa.title")}</h1></CardTitle>
            <CardDescription>{t("auth.mfa.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onMfaSubmit} className="space-y-8">
              <div className="gap-3 grid">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("auth.mfa.code")}
                  </label>
                  <MfaCodeInput
                    key={mfaEmail || "mfa-input"}
                    value={mfaCode}
                    onChange={(value) => {
                      setMfaCode(value);
                      setMfaError("");
                    }}
                    disabled={mfaVerifyMutation.isPending}
                  />
                  {mfaError && (
                    <p className="text-sm text-red-500 text-center">
                      {mfaError}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-3">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={
                      mfaVerifyMutation.isPending || mfaCode.length !== 6
                    }
                  >
                    {mfaVerifyMutation.isPending
                      ? t("auth.mfa.verifying")
                      : t("auth.mfa.verify")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setMfaRequired(false);
                      setMfaEmail("");
                      setMfaCode("");
                      setMfaError("");
                    }}
                  >
                    {t("auth.mfa.backToLogin")}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-7 sm:gap-8", className)} {...props}>
      <AuthBrandMark />
      <Card className="glass-panel border-white/60">
        <CardHeader>
          <CardTitle>
            <h1>{linkContext ? t("auth.social.linkConfirmTitle") : t("auth.login")}</h1>
          </CardTitle>
          <CardDescription>
            {linkContext
              ? t("auth.social.linkConfirmMessage", {
                  provider: linkContext.provider === "google" ? "Google" : "Apple",
                })
              : t("auth.enterEmail")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...loginForm}>
            <form
              onSubmit={loginForm.handleSubmit(onLoginSubmit)}
              className="space-y-8"
            >
              <div className="gap-3 grid">
                {showAccountConflict && (
                  <div
                    role="alert"
                    className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground"
                  >
                    {t("auth.social.accountConflict")}
                    {conflictExpired && (
                      <p className="mt-1">{t("auth.social.expired")}</p>
                    )}
                  </div>
                )}
                {loginErrorKey && (
                  <div
                    role="alert"
                    className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
                  >
                    {t(loginErrorKey as never)}
                  </div>
                )}
                <FormInput
                  name="usernameOrEmail"
                  control={loginForm.control}
                  label={t("auth.email")}
                  placeholder="m@example.com"
                  inputProps={linkContext ? { readOnly: true } : undefined}
                />
                <FormInput
                  name="password"
                  control={loginForm.control}
                  label={t("auth.password")}
                  placeholder={t("auth.password")}
                  type="password"
                />
                {!linkContext && (
                  <div className="text-sm text-right">
                    <Link
                      href={`/${locale}/auth/forgot-password`}
                      className="underline underline-offset-4"
                    >
                      {t("auth.forgotPassword")}
                    </Link>
                  </div>
                )}
                <div className="flex flex-col gap-3">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loginMutation.isPending || linkPending}
                  >
                    {loginMutation.isPending || linkPending
                      ? t("auth.loggingIn")
                      : linkContext
                        ? t("auth.social.linkConfirmButton", {
                            provider: linkContext.provider === "google" ? "Google" : "Apple",
                          })
                        : t("auth.login")}
                  </Button>
                </div>
              </div>
              <div className="mt-4 text-sm text-center">
                {t("auth.noAccount")}{" "}
                <Link
                  href={`/${locale}/auth/register`}
                  className="underline underline-offset-4"
                >
                  {t("auth.register")}
                </Link>
              </div>
            </form>
          </Form>
          {!linkContext && (
            <div className="mt-6 flex flex-col gap-3">
              <GoogleSignInButton />
              <AppleSignInButton withDivider={false} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
